/**
 * Consent, rendered per identity.
 *
 * The design puts `.oc-consent` on contact_identities for a reason: the model
 * says consent belongs to an address, and a UI that shows one badge per person
 * makes that a lie the user discovers later — usually when they mail someone
 * who unsubscribed.
 */

import type { ConsentActor, ConsentSource, ConsentStatus } from "../api/types";

const LABEL: Record<ConsentStatus, string> = {
  subscribed: "Subscribed",
  pending: "Pending",
  unsubscribed: "Unsubscribed",
  unknown: "Unknown",
};

/**
 * Glyphs shared with the message states, where the meaning is shared.
 *
 * `subscribed` is a tick in a ring for the same reason `delivered` is, and
 * `pending` is a clock for the same reason `queued` is. Inventing a second mark
 * for "confirmed" and a third for "waiting" would be two vocabularies for one
 * idea — which is the thing that actually confuses people, not the reuse.
 *
 * `unknown` is the one that earns its own mark. It looks harmless and behaves
 * exactly like an unsubscribe, so an empty cell would be a lie.
 */
const ICON: Record<ConsentStatus, string> = {
  subscribed: "oc-status-check-circle",
  pending: "oc-status-clock",
  unsubscribed: "oc-status-minus-circle",
  unknown: "oc-status-question-circle",
};

/**
 * What each state actually means, for the tooltip. `unknown` is the one worth
 * spelling out: it looks harmless and is the reason a campaign silently reaches
 * fewer people than the list suggests.
 */
const MEANING: Record<ConsentStatus, string> = {
  subscribed: "This address may be sent marketing on this channel.",
  pending: "Consent was requested and not yet confirmed — a double opt-in waiting on the click.",
  unsubscribed: "They asked to stop. Campaigns skip this address.",
  unknown:
    "Nothing was ever recorded for this address. Not permission — campaigns skip it, the same as an unsubscribe.",
};

export const SOURCE_LABEL: Record<ConsentSource, string> = {
  import: "a CSV import",
  api: "the API",
  form: "a signup form",
  double_opt_in: "a confirmed double opt-in",
  manual: "someone on the team",
  unsubscribe_link: "the unsubscribe link",
  complaint: "a spam complaint",
};

/**
 * Who made a decision, for the line under it in a consent history. `source`
 * says how; this says who. Null for a decision recorded before the actor was
 * kept, which then says nothing rather than guessing.
 */
export function actorLabel(actor?: ConsentActor | null): string | null {
  if (!actor) return null;
  switch (actor.kind) {
    case "member":
      return actor.label ?? "a member of the team";
    case "api_key":
      return actor.label ? `the API key “${actor.label}”` : "an API key";
    case "recipient":
      return "the address owner";
    case "form":
      return actor.label ? `the form “${actor.label}”` : "a signup form";
  }
}

export function Consent({ status }: { status: ConsentStatus }) {
  return (
    <span className="oc-consent" data-consent={status} title={MEANING[status]}>
      <svg className="oc-consent__glyph" aria-hidden="true" focusable="false">
        <use href={`#${ICON[status]}`} />
      </svg>
      {LABEL[status]}
    </span>
  );
}

/**
 * The bare glyph, for the consent history timeline.
 *
 * Replaces a hand-rolled copy in ContactDrawer that carried its own shape map
 * and a `glyphStatus` adapter translating consent onto the *message* palette —
 * which meant `unknown` was drawn as `unsubscribed` and the two became
 * indistinguishable. Keyed on data-consent now, so it uses its own colours.
 *
 * aria-hidden because the timeline label names the transition beside it.
 */
export function ConsentGlyph({ status }: { status: ConsentStatus }) {
  return (
    <svg className="oc-consent-glyph" data-consent={status} aria-hidden="true" focusable="false">
      <use href={`#${ICON[status]}`} />
    </svg>
  );
}

/** True when a campaign would actually go to this address. */
export function isSendable(status: ConsentStatus): boolean {
  return status === "subscribed";
}
